import { Trans, useTranslation } from "react-i18next";

import { LegalBrand, LegalPageLayout, List, Section } from "./LegalPageLayout";

const CONTACT = "hello@delgentic.com";

/**
 * Where the calendar-provider disclosure points. Google requires an affirmative,
 * linked statement that the app's use of data received from its APIs follows
 * this policy, including the Limited Use requirements.
 */
const GOOGLE_USER_DATA_POLICY =
  "https://developers.google.com/terms/api-services-user-data-policy";

/**
 * Plain-English, GDPR-governed privacy notice. Mostly generic — we describe
 * categories of processing rather than naming every vendor or hosting location.
 * Keep in sync with what the app actually does if data flows change.
 *
 * <p><b>The calendar bullet is the deliberate exception to that generality.</b>
 * Both Google Calendar scopes we request are classified Sensitive, so the
 * integration goes through OAuth verification, and the reviewer checks this page
 * for the provider by name, what each permission is used for, and the Limited
 * Use statement. Keep it specific. Outlook is scaffolded already
 * ({@code microsoftOauthClientId} in {@code cdk.json}), so the bullet is worded
 * to take a second provider as an addition rather than a rewrite.</p>
 *
 * <p>Copy is externalised to the i18n bundles (en/da) under the {@code privacy.*}
 * keys. Paragraphs carry inline markup (<strong>, <code>, a mailto link, an
 * external policy link), so they render through {@link Trans} with a shared
 * component map rather than a plain {@code t()} call.</p>
 */
export function PrivacyPage({
  brand,
  onSignup,
}: {
  brand: LegalBrand;
  onSignup: () => void;
}) {
  const { t } = useTranslation();

  // One component map shared by every rich paragraph on the page.
  const rich = {
    strong: <strong />,
    code: <code />,
    mail: <a className="underline" href={`mailto:${CONTACT}`} />,
    // Generic external link. The calendar bullet uses it for Google's user-data
    // policy; the Microsoft equivalent will reuse the same tag.
    policy: (
      <a
        className="underline"
        href={GOOGLE_USER_DATA_POLICY}
        target="_blank"
        rel="noreferrer"
      />
    ),
  };

  return (
    <LegalPageLayout
      title={t("privacy.title")}
      updated={t("privacy.updated")}
      brand={brand}
      onSignup={onSignup}
    >
      <Section heading={t("privacy.who.heading")}>
        <p>
          <Trans i18nKey="privacy.who.body" components={rich} />
        </p>
      </Section>

      <Section heading={t("privacy.collect.heading")}>
        <List>
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <li key={i}>
              <Trans i18nKey={`privacy.collect.items.${i}`} components={rich} />
            </li>
          ))}
        </List>
      </Section>

      <Section heading={t("privacy.cardVisibility.heading")}>
        <p>
          <Trans i18nKey="privacy.cardVisibility.body" components={rich} />
        </p>
      </Section>

      <Section heading={t("privacy.use.heading")}>
        <p>
          <Trans i18nKey="privacy.use.body" components={rich} />
        </p>
      </Section>

      <Section heading={t("privacy.ai.heading")}>
        <p>
          <Trans i18nKey="privacy.ai.body" components={rich} />
        </p>
      </Section>

      {/* Named recipients, not "a small number of trusted providers". Google's OAuth review
          rejected the old wording outright: "Your privacy policy does not state with whom you
          share, transfer, or disclose Google user data." The closing paragraph is the one they
          look for — it states the transfer boundary for Workspace data explicitly, rather than
          leaving the Limited Use commitment as a recital. */}
      <Section heading={t("privacy.sharing.heading")}>
        <p>
          <Trans i18nKey="privacy.sharing.intro" components={rich} />
        </p>
        <List>
          {[0, 1, 2, 3].map((i) => (
            <li key={i}>
              <Trans i18nKey={`privacy.sharing.items.${i}`} components={rich} />
            </li>
          ))}
        </List>
        <p>
          <Trans i18nKey="privacy.sharing.outro" components={rich} />
        </p>
      </Section>

      <Section heading={t("privacy.peers.heading")}>
        <p>
          <Trans i18nKey="privacy.peers.body" components={rich} />
        </p>
      </Section>

      {/*
        The retention boundary for a guest who has no account (D-29). It says the honest thing
        rather than the comfortable one: credentials are discarded, identity is not. Once a meeting
        exists, the guest's name and address are on somebody else's calendar entry, in the .ics
        ATTENDEE list and in the host's record of who asked — and a host with an appointment and no
        idea who it is with has been failed. This is a public commitment, so it lives here and not
        only in the design note.
      */}
      <Section heading={t("privacy.guests.heading")}>
        <p>
          <Trans i18nKey="privacy.guests.body" components={rich} />
        </p>
      </Section>

      <Section heading={t("privacy.storage.heading")}>
        <p>
          <Trans i18nKey="privacy.storage.body" components={rich} />
        </p>
      </Section>

      <Section heading={t("privacy.rights.heading")}>
        <p>
          <Trans i18nKey="privacy.rights.body" components={rich} />
        </p>
      </Section>

      <Section heading={t("privacy.changes.heading")}>
        <p>
          <Trans i18nKey="privacy.changes.body" components={rich} />
        </p>
      </Section>
    </LegalPageLayout>
  );
}
